import { useMemo } from 'react';
import { useAppSelector } from '../../redux/hooks';
import {
  dataTableMeasuresSelector,
  measuresSelector,
  stepsSelector,
} from '../../redux/selectors/builder';
import { QueryType } from '../../utils/apollo/graphqlSchema';
import {
  isEmptyEventCondition,
  isEmptyEventConditions,
} from '../../utils/builder';
import useQueryTypeFromLocation from '../../utils/routes/useQueryTypeFromLocation';
import { assertNotNil } from '../../utils/types';

/*
  Given the query builder page the user is on,
  perform validation of whether the most basic form of each query builder is complete.
  Eg. At least one event selected in Query, or 2 steps in funnel, etc.

  When the basic builder is complete, render the additional dropzones in
  report filter, breakdowns, segments
**/
export default () => {
  const queryType = useQueryTypeFromLocation();
  assertNotNil(queryType);
  const measures = useAppSelector(measuresSelector);
  const dataTableMeasures = useAppSelector(dataTableMeasuresSelector);
  const steps = useAppSelector(stepsSelector);
  const paths = useAppSelector(({ builder }) => builder.paths);
  const retentionConditions = useAppSelector(
    ({ builder }) => builder.retention.retentionConditions,
  );

  const isBasicBuilderComplete = useMemo(() => {
    if (queryType === QueryType.Expression) {
      return (
        measures.length > 0 &&
        !isEmptyEventCondition(measures[0].eventConditions[0])
      );
    }

    if (queryType === QueryType.Funnel) {
      return (
        steps.length >= 2 &&
        steps[0].eventConditions &&
        steps[1].eventConditions &&
        !isEmptyEventConditions(steps[0].eventConditions) &&
        !isEmptyEventConditions(steps[1].eventConditions)
      );
    }

    if (queryType === QueryType.Path) {
      return (
        paths.length === 2 &&
        !isEmptyEventConditions(paths[0]) &&
        !isEmptyEventConditions(paths[1])
      );
    }

    if (queryType === QueryType.Retention) {
      return (
        retentionConditions.length === 2 &&
        !isEmptyEventConditions(retentionConditions[0]) &&
        !isEmptyEventConditions(retentionConditions[1])
      );
    }

    if (queryType === QueryType.DataTable) {
      return (
        dataTableMeasures.length > 0 &&
        (!!dataTableMeasures[0].measureId ||
          !isEmptyEventCondition(dataTableMeasures[0].eventConditions[0]))
      );
    }

    return false;
  }, [
    measures,
    dataTableMeasures,
    queryType,
    steps,
    paths,
    retentionConditions,
  ]);

  return {
    isBasicBuilderComplete,
  };
};
